// Hook that fetches franchise/territory color config once and returns a
// helper `colorFor(territory)` that resolves the project's `territory`
// string (e.g. "Fox Chase/Torresdale (10090)") to the franchise color hex
// configured at Setup → Franchises. Falls back to '#6b7280' (gray-500)
// if the territory isn't recognised yet.
import { useEffect, useState, useCallback } from 'react';
import { readResponse, readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_COLOR = '#6b7280'; // gray-500

let cachedFranchises = null;
let inflightPromise = null;

const fetchFranchises = async () => {
  if (cachedFranchises) return cachedFranchises;
  if (inflightPromise) return inflightPromise;
  inflightPromise = (async () => {
    try {
      const res = await fetch(`${API_URL}/api/franchise-locations`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { data } = await readResponse(res);
      cachedFranchises = data.franchises || [];
      return cachedFranchises;
    } catch {
      cachedFranchises = [];
      return cachedFranchises;
    } finally {
      inflightPromise = null;
    }
  })();
  return inflightPromise;
};

const buildLookup = (franchises) => {
  // Two parallel lookups: by franchise number ("10090") and by lowercased
  // name ("Fox Chase/Torresdale"). Number wins when the territory string
  // contains it.
  const byNumber = new Map();
  const byName = new Map();
  (franchises || []).forEach((f) => {
    if (!f) return;
    if (f.number) byNumber.set(String(f.number).trim(), f.color || DEFAULT_COLOR);
    if (f.name) byName.set(String(f.name).trim().toLowerCase(), f.color || DEFAULT_COLOR);
  });
  return { byNumber, byName };
};

export const useFranchiseColors = () => {
  const [franchises, setFranchises] = useState(cachedFranchises || []);

  useEffect(() => {
    if (cachedFranchises) {
      setFranchises(cachedFranchises);
      return;
    }
    fetchFranchises().then(setFranchises);
  }, []);

  const colorFor = useCallback(
    (territory) => {
      const t = String(territory || '').trim();
      if (!t) return DEFAULT_COLOR;
      const { byNumber, byName } = buildLookup(franchises);
      // Try the embedded franchise number first: e.g. "Foo (10090)".
      const numMatch = t.match(/\((\d+)\)/);
      if (numMatch && byNumber.has(numMatch[1])) return byNumber.get(numMatch[1]);
      // Strip trailing "(NNNNN)" for name match.
      const cleanName = t.replace(/\s*\(\d+\)\s*$/, '').trim().toLowerCase();
      if (byName.has(cleanName)) return byName.get(cleanName);
      // Loose partial match (case-insensitive substring) as a last resort.
      for (const [name, color] of byName.entries()) {
        if (cleanName.includes(name) || name.includes(cleanName)) return color;
      }
      return DEFAULT_COLOR;
    },
    [franchises]
  );

  return { colorFor };
};

export default useFranchiseColors;
